import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

/**
 * camelCase, unlike the PascalCase LiveFilters page reads.
 *
 * GET /v1/live/frames runs every record through the backend's shared frame serializer, so the
 * catalog, the session snapshot, the socket ack and FRAME_CHANGED all carry this identical shape.
 * PascalCase aliases are accepted on input for compatibility but are not what this endpoint emits.
 */
type LiveFrame = {
  frameId: string;
  name: string;
  movUrl: string;
  webmUrl: string;
  thumbnailUrl: string;
  isActive: boolean;
  createdAt?: string;
};

const LiveFrames: React.FC = () => {
  const navigate = useNavigate();
  const [frames, setFrames] = useState<LiveFrame[]>([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [togglingId, setTogglingId] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const fetchFrames = useCallback(
    async (pageNum: number) => {
      const headers = headerWithAuth();
      if (!headers) return;

      setLoading(true);
      try {
        // activeOnly=false: the dashboard manages the whole catalog, including deactivated frames.
        // The app's picker uses the default (true) so it never offers one.
        const res = await fetch(`${liveApiBase}/frames?page=${pageNum}&limit=${limit}&activeOnly=false`, {
          headers,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to load frames');
        const data = json.data as { frames: LiveFrame[]; total: number; hasMore: boolean };
        setFrames(data.frames || []);
        setTotal(data.total || 0);
        setHasMore(Boolean(data.hasMore));
      } catch (error) {
        toast.error((error as Error).message || 'Failed to fetch frames');
      } finally {
        setLoading(false);
      }
    },
    [headerWithAuth]
  );

  useEffect(() => {
    fetchFrames(page);
  }, [fetchFrames, page]);

  /**
   * Deactivating only stops the frame being NEWLY selected.
   *
   * Streams already using it keep rendering it until their host clears it — the session stores its
   * own copy of the frame. That is deliberate: pulling a border off a live broadcast because an
   * admin flipped a switch is the worse outcome.
   */
  const handleToggleActive = useCallback(
    async (frame: LiveFrame) => {
      const headers = headerWithAuth();
      if (!headers) return;

      const next = !frame.isActive;
      setTogglingId(frame.frameId);
      try {
        const res = await fetch(`${liveApiBase}/frames/${encodeURIComponent(frame.frameId)}`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ isActive: next }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to update frame');
        toast.success(next ? 'Frame activated' : 'Frame deactivated — live streams already using it keep it');
        setFrames((prev) =>
          prev.map((f) => (f.frameId === frame.frameId ? { ...f, isActive: next } : f))
        );
      } catch (error) {
        toast.error((error as Error).message || 'Failed to update frame');
      } finally {
        setTogglingId('');
      }
    },
    [headerWithAuth]
  );

  const handleDelete = useCallback(
    async (frameId: string) => {
      // Deleting removes the catalog record outright, so confirm — unlike deactivating, it cannot
      // be undone from this screen.
      if (!window.confirm('Delete this frame permanently? Deactivate instead if you only want to hide it.')) {
        return;
      }

      const headers = headerWithAuth();
      if (!headers) return;

      setDeletingId(frameId);
      try {
        const res = await fetch(`${liveApiBase}/frames/${encodeURIComponent(frameId)}`, {
          method: 'DELETE',
          headers,
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Failed to delete frame');
        toast.success(json.message || 'Frame deleted');
        setFrames((prev) => prev.filter((f) => f.frameId !== frameId));
        setTotal((prev) => Math.max(0, prev - 1));
      } catch (error) {
        toast.error((error as Error).message || 'Failed to delete frame');
      } finally {
        setDeletingId('');
      }
    },
    [headerWithAuth]
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Live Frames</h1>
            <p className='text-sm text-gray-400'>
              Transparent animated borders drawn over the video, below comments and gifts.
            </p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='text-sm text-gray-400'>
              {loading ? 'Loading...' : `${total} frame${total !== 1 ? 's' : ''}`}
            </div>
            <button
              onClick={() => navigate('/live-frames/new')}
              className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition'
            >
              Upload Frame
            </button>
          </div>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Fetching frames...</div>
        ) : frames.length === 0 ? (
          <div className='text-sm text-gray-400'>No frames found.</div>
        ) : (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
              {frames.map((frame) => (
                <div
                  key={frame.frameId}
                  className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden shadow-lg'
                >
                  {/* Checkerboard, so a frame whose alpha channel is broken looks wrong here rather
                      than on a live stream. */}
                  <div className='h-48 flex items-center justify-center overflow-hidden bg-[conic-gradient(#334155_0_25%,#1e293b_0_50%,#334155_0_75%,#1e293b_0)] bg-[length:20px_20px]'>
                    {frame.thumbnailUrl ? (
                      <img src={frame.thumbnailUrl} alt={frame.name} className='w-full h-full object-contain' />
                    ) : (
                      <span className='text-xs text-gray-500'>No thumbnail</span>
                    )}
                  </div>

                  <div className='p-4 space-y-2'>
                    <div className='text-sm font-semibold text-white'>{frame.name}</div>
                    <div className='text-xs text-gray-400 truncate'>ID: {frame.frameId}</div>

                    <div className='flex items-center gap-2 text-xs'>
                      <span
                        className={`px-2 py-0.5 rounded-full border ${
                          frame.isActive
                            ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                            : 'text-red-300 border-red-400/40 bg-red-500/10'
                        }`}
                      >
                        {frame.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {frame.createdAt && (
                        <span className='text-gray-500'>{new Date(frame.createdAt).toLocaleDateString()}</span>
                      )}
                    </div>

                    <div className='space-y-1 pt-1'>
                      <a
                        href={frame.movUrl}
                        target='_blank'
                        rel='noreferrer'
                        className='text-xs text-[#FF9C15] hover:underline break-all block'
                      >
                        iOS .mov
                      </a>
                      <a
                        href={frame.webmUrl}
                        target='_blank'
                        rel='noreferrer'
                        className='text-xs text-sky-300 hover:underline break-all block'
                      >
                        Android .webm
                      </a>
                    </div>

                    <div className='flex gap-2 pt-2'>
                      <button
                        onClick={() => navigate(`/live-frames/${encodeURIComponent(frame.frameId)}/edit`, { state: { frame } })}
                        className='flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20 text-white transition'
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(frame)}
                        disabled={togglingId === frame.frameId}
                        className='flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-white/10 hover:bg-white/20 text-white transition disabled:opacity-60'
                      >
                        {togglingId === frame.frameId
                          ? 'Saving...'
                          : frame.isActive
                          ? 'Deactivate'
                          : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(frame.frameId)}
                        disabled={deletingId === frame.frameId}
                        className='flex-1 px-3 py-2 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-60'
                      >
                        {deletingId === frame.frameId ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className='flex items-center justify-between mt-8'>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1 || loading}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                Previous
              </button>
              <span className='text-xs text-gray-400'>Page {page}</span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={!hasMore || loading}
                className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LiveFrames;
